import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { Footer } from './Footer';
import { Header } from './Header';

export interface PublicLayoutProps {
  currentUrl: string;
  children: ReactNode;
}

/**
 * The shell every public page renders inside.
 *
 * **The public site is light-only** (Bitwize brand reference, 2026-09): the
 * same paper page, hairline borders and navy-band sections the admin already
 * had tokens for. There is deliberately no theme control in the public
 * header to match: see `Header`. Individual bands that need the dark
 * register — the home hero, the closing CTA — scope `data-theme="dark"` to
 * just themselves rather than this shell carrying it for the whole page.
 *
 * Three other things here are not decoration:
 *
 *   - The skip link is the first focusable element on the page. A keyboard user
 *     should not tab through the whole navigation on every page.
 *   - There is exactly one `<main>`, and it carries `id="main"` so the skip
 *     link has a target and assistive technology has a landmark.
 *   - The layout is a column with the footer pushed down by `mt-auto`, so a
 *     short page does not leave the footer floating mid-screen.
 */
export function PublicLayout({ currentUrl, children }: PublicLayoutProps) {
  const { t } = useI18n();

  return (
    <div className="relative flex min-h-dvh flex-col bg-bg text-text">
      <a href="#main" className="hs-skip-link">
        {t('common.actions.skipToContent')}
      </a>

      <Header currentUrl={currentUrl} />

      <main id="main" className="flex-1">
        {children}
      </main>

      <Footer />
    </div>
  );
}
